import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, type Employee } from '@hrms/shared';
import { api } from '../../../lib/api';
import { humanize } from '../../../lib/date';
import { useMut } from '../../../lib/query';
import { Avatar, Button, Card, Columns, DataTable, DescList, ErrorState, Grid, Loading, Modal, PageHeader, Screen, Stack, StatusBadge, Text, TextField } from '../../../ui';

const EDITABLE: { key: keyof Employee; label: string; keyboard?: 'phone-pad' | 'email-address' | 'number-pad' }[] = [
  { key: 'phone', label: 'Mobile', keyboard: 'phone-pad' },
  { key: 'personal_email', label: 'Personal email', keyboard: 'email-address' },
  { key: 'marital_status', label: 'Marital status' },
  { key: 'blood_group', label: 'Blood group' },
  { key: 'bank_name', label: 'Bank name' },
  { key: 'bank_account_number', label: 'Bank account number', keyboard: 'number-pad' },
  { key: 'bank_ifsc', label: 'IFSC' },
  { key: 'address', label: 'Address' },
];

export function ProfileDetails({ emp }: { emp: Employee }) {
  const j = emp.current_job;
  return (
    <Stack>
      <Card title="Personal">
        <DescList
          items={[
            ['Full name', emp.full_name],
            ['Employee code', emp.emp_code],
            ['Work email', emp.email],
            ['Personal email', emp.personal_email],
            ['Mobile', emp.phone],
            ['Date of birth', fmtDate(emp.date_of_birth)],
            ['Gender', humanize(emp.gender)],
            ['Marital status', humanize(emp.marital_status)],
            ['Blood group', emp.blood_group],
            ['Address', emp.address],
          ]}
        />
      </Card>
      <Card title="Job">
        <DescList
          items={[
            ['Designation', j?.designation?.name ?? emp.designation],
            ['Department', j?.department?.name ?? emp.department],
            ['Location', j?.location?.name ?? emp.location],
            ['Legal entity', j?.legal_entity?.name],
            ['Grade', j?.grade?.name],
            ['Reporting manager', j?.manager?.full_name],
            ['Employment type', humanize(j?.employment_type)],
            ['Date of joining', fmtDate(emp.date_of_joining)],
            ['Confirmation', fmtDate(emp.confirmation_date)],
          ]}
        />
      </Card>
      <Card title="Statutory & bank" subtitle="Sensitive values are masked">
        <DescList
          items={[
            ['PAN', emp.pan],
            ['Aadhaar', emp.aadhaar],
            ['UAN', emp.uan],
            ['ESIC', emp.esic_number],
            ['Bank', emp.bank_name],
            ['Account', emp.bank_account_number],
            ['IFSC', emp.bank_ifsc],
          ]}
        />
      </Card>
    </Stack>
  );
}

export function MyProfileScreen() {
  const q = useQuery({ queryKey: ['me', 'profile'], queryFn: () => api.ess.myProfile() });
  const changes = useQuery({ queryKey: ['profile-changes'], queryFn: () => api.ess.profileChanges() });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const submit = useMut(
    () => {
      const diff = Object.fromEntries(Object.entries(form).filter(([k, v]) => v !== ((q.data?.[k as keyof Employee] as string) ?? '')));
      return api.ess.requestProfileChange(diff);
    },
    { invalidate: [['profile-changes']], success: 'Change request submitted for approval', onSuccess: () => setOpen(false) },
  );
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <Screen><PageHeader title="My profile" /><ErrorState error={q.error} onRetry={() => q.refetch()} /></Screen>;
  const emp = q.data;
  const dirty = Object.entries(form).some(([k, v]) => v !== ((emp[k as keyof Employee] as string) ?? ''));
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()}>
      <PageHeader
        title="My profile"
        actions={
          <Button
            icon="edit"
            variant="primary"
            title="Request change"
            onPress={() => {
              setForm(Object.fromEntries(EDITABLE.map((f) => [f.key, (emp[f.key] as string) ?? ''])));
              setOpen(true);
            }}
          />
        }
      />
      <Columns>
        <ProfileDetails emp={emp} />
        <Stack>
          <Card>
            <View style={{ flexDirection: 'row', gap: 14, alignItems: 'center' }}>
              <Avatar name={emp.full_name} size={56} />
              <View style={{ flex: 1, gap: 2 }}>
                <Text weight="semibold" size={16}>
                  {emp.full_name}
                </Text>
                <Text variant="muted">{emp.designation}</Text>
                <StatusBadge status={emp.status} />
              </View>
            </View>
          </Card>
          <Text variant="title">Change requests</Text>
          <DataTable
            rows={changes.data}
            loading={changes.isLoading}
            emptyTitle="No change requests"
            columns={[
              { key: 'created_at', title: 'Date', width: 110, render: (r) => fmtDate(r.created_at), sortValue: (r) => r.created_at },
              { key: 'fields', title: 'Fields', width: 160, render: (r) => Object.keys(r.changes).map(humanize).join(', '), sortable: false },
              { key: 'status', title: 'Status', width: 100, render: (r) => <StatusBadge status={r.status} />, sortValue: (r) => r.status },
            ]}
          />
        </Stack>
      </Columns>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Request profile change"
        subtitle="Changes go through an approval workflow before they are applied."
        footer={
          <>
            <Button title="Cancel" onPress={() => setOpen(false)} />
            <Button title="Submit for approval" variant="primary" disabled={!dirty} loading={submit.isPending} onPress={() => submit.mutate(undefined)} />
          </>
        }
      >
        <Grid min={220} gap={12}>
          {EDITABLE.filter((f) => f.key !== 'address').map((f) => (
            <TextField key={f.key} label={f.label} keyboardType={f.keyboard} autoCapitalize={f.keyboard === 'email-address' ? 'none' : undefined} value={form[f.key] ?? ''} onChangeText={(v) => setForm({ ...form, [f.key]: v })} />
          ))}
        </Grid>
        <TextField label="Address" multiline value={form.address ?? ''} onChangeText={(v) => setForm({ ...form, address: v })} />
      </Modal>
    </Screen>
  );
}
